import React, { useEffect } from 'react';
import { Eye, Users, Building, Heart, Shield, CheckCircle, ArrowRight, Zap,TreePine } from 'lucide-react';

const VisionPage: React.FC = () => {
    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
    }, []);

    const keyAnnouncements = [
        "Eau et Électricité comme Droit fondamental",
        "Santé souveraine et hub de tourisme médical",
        "Pôle numérique d'excellence",
        "Centres de Redressement pour les jeunes",
        "Statut solide pour artistes et sportifs",
        "Finalisation du réseau routier national",
        "Règlementation du marché immobilier",
        "Programme 'Un Gabonais, Un Titre Foncier'",
        "Cinq pôles agricoles régionaux",
        "Crédit Agricole à Taux Réduit (CATER)",
        "Soutien au retour de la diaspora",
        "Plateforme 'Okoumé Talent'",
        "Digitalisation de l'administration",
        "Projet 'Aidez-moi à vous aider' - 20 milliards FCFA",
        "Désenclavement du mapane"
    ];

    const programPillars = [
        {
            number: 1,
            title: "Électricité, Eau et Réformes du Secteur",
            icon: Zap,
            color: "text-primary-blue",
            bgColor: "bg-primary-blue",
            lightBg: "bg-primary-blue/10",
            description: "Solutions immédiates et durables pour l'accès universel à l'électricité et à l'eau potable",
            highlights: [
                "Acquisition de générateurs d'urgence",
                "Modernisation du réseau électrique",
                "Sécurisation de la production d'eau",
                "Réforme de la SEEG"
            ]
        },
        {
            number: 2,
            title: "Entrepreneuriat et Employabilité des Jeunes",
            icon: Users,
            color: "text-primary-green",
            bgColor: "bg-primary-green",
            lightBg: "bg-primary-green/10",
            description: "Soutien à l'entrepreneuriat et formation adaptée aux besoins du marché",
            highlights: [
                "Incubateurs et accélérateurs",
                "Renforcement de la BCEG",
                "Simplification administrative",
                "Label 'Entreprise Citoyenne'"
            ]
        },
        {
            number: 3,
            title: "Logements, Infrastructures de Transport et Numériques",
            icon: Building,
            color: "text-primary-yellow",
            bgColor: "bg-primary-yellow",
            lightBg: "bg-primary-yellow/10",
            description: "Modernisation des infrastructures pour une croissance économique durable",
            highlights: [
                "Réforme de la politique foncière",
                "Modernisation du réseau routier",
                "Déploiement de la fibre optique",
                "Datacenters nationaux"
            ]
        },
        {
            number: 4,
            title: "Capital Humain et Justice Sociale",
            icon: Heart,
            color: "text-primary-blue",
            bgColor: "bg-primary-blue",
            lightBg: "bg-primary-blue/10",
            description: "Investissement dans la santé, l'éducation et l'inclusion sociale",
            highlights: [
                "Réforme du système de santé",
                "Pôles spécialisés médicaux",
                "Refonte du système éducatif",
                "Soutien aux personnes vulnérables"
            ]
        },
        {
            number: 5,
            title: "Économie et Développement Durable",
            icon: TreePine,
            color: "text-primary-green",
            bgColor: "bg-primary-green",
            lightBg: "bg-primary-green/10",
            description: "Diversification économique et valorisation du capital naturel",
            highlights: [
                "Diversification économique",
                "Développement de l'économie rurale",
                "Valorisation des ressources naturelles",
                "Économie verte"
            ]
        },
        {
            number: 6,
            title: "Gouvernance et Institutions",
            icon: Shield,
            color: "text-primary-yellow",
            bgColor: "bg-primary-yellow",
            lightBg: "bg-primary-yellow/10",
            description: "Restauration de l'État de droit et modernisation de l'administration",
            highlights: [
                "Réforme du système judiciaire",
                "Administration performante",
                "Décentralisation effective",
                "Modernisation des Forces de Défense"
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section avec image d'arrière-plan */}
            <div className="relative pt-24 pb-16 px-4 min-h-[70vh] flex items-center justify-center overflow-hidden">
                {/* Image d'arrière-plan avec overlay */}
                <div className="absolute inset-0 z-0">
                    <div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                        style={{
                            backgroundImage: `url('/images/herobg.jpg')`,
                            filter: 'brightness(0.3)',
                        }}
                    />

                    {/* Overlay gradient UDB */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-blue/80 via-primary-green/60 to-primary-yellow/40"></div>

                    {/* Overlay noir pour plus de contraste */}
                    <div className="absolute inset-0 bg-black/30"></div>
                </div>

                {/* Contenu */}
                <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
                    <div className="inline-flex items-center space-x-2 text-primary-yellow text-sm mb-6 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                        <Eye className="w-4 h-4" />
                        <span className="font-medium">Notre Vision</span>
                    </div>

                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6">
                        Un Gabon moderne et prospère
                    </h1>

                    <div className="w-16 h-1 bg-primary-yellow mx-auto mb-8"></div>

                    <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto opacity-95 font-light">
                        "Ensemble, nous avons le pouvoir de bâtir le Gabon que nos enfants méritent"
                    </p>

                    <p className="opacity-90 max-w-3xl mx-auto leading-relaxed text-lg">
                        Une vision ambitieuse pour transformer notre nation à travers des réformes
                        profondes et une gouvernance exemplaire, plaçant le citoyen au cœur de
                        toutes nos actions.
                    </p>
                </div>
            </div>

            {/* 6 Piliers avec couleurs */}
            <section className="py-16">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-light text-gray-900 mb-4">
                            Les 6 Piliers de Notre Programme
                        </h2>
                        <div className="w-16 h-1 bg-primary-yellow mx-auto mb-6"></div>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Un projet de société structuré autour de six axes stratégiques
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {programPillars.map((pillar, index) => {
                            const Icon = pillar.icon;
                            return (
                                <div
                                    key={index}
                                    className={`${pillar.lightBg} p-8 border-l-4 ${pillar.bgColor} hover:shadow-lg transition-all duration-300`}
                                >
                                    <div className="flex items-start space-x-6">
                                        <div className={`w-14 h-14 ${pillar.bgColor} flex items-center justify-center text-white flex-shrink-0`}>
                                            <Icon className="w-7 h-7" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-3">
                                                <span className={`text-3xl font-light ${pillar.color} opacity-60`}>0{pillar.number}</span>
                                                <span className={`${pillar.color} font-semibold text-xs uppercase tracking-wider`}>PILIER</span>
                                            </div>

                                            <h3 className={`text-xl font-semibold ${pillar.color} mb-4 leading-tight`}>
                                                {pillar.title}
                                            </h3>

                                            <p className="text-gray-700 mb-6 leading-relaxed">
                                                {pillar.description}
                                            </p>

                                            <div className="space-y-2">
                                                {pillar.highlights.map((highlight, idx) => (
                                                    <div key={idx} className="flex items-center space-x-3 text-sm">
                                                        <CheckCircle className={`w-4 h-4 ${pillar.color} flex-shrink-0`} />
                                                        <span className="text-gray-700">{highlight}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* 15 Engagements avec couleurs alternées */}
            <section className="py-16 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-light text-gray-900 mb-4">
                            15 Engagements Chocs
                        </h2>
                        <div className="w-16 h-1 bg-primary-yellow mx-auto mb-6"></div>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Nos annonces clés pour transformer le Gabon avec ambition, audace et pragmatisme
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {keyAnnouncements.map((announcement, index) => {
                            const colors = [
                                { bg: 'bg-primary-blue/10', accent: 'bg-primary-blue', text: 'text-primary-blue' },
                                { bg: 'bg-primary-green/10', accent: 'bg-primary-green', text: 'text-primary-green' },
                                { bg: 'bg-primary-yellow/10', accent: 'bg-primary-yellow', text: 'text-primary-yellow' }
                            ];
                            const colorSet = colors[index % 3];

                            return (
                                <div
                                    key={index}
                                    className={`${colorSet.bg} p-6 border-l-4 ${colorSet.accent} hover:shadow-md transition-all duration-300`}
                                >
                                    <div className="flex items-start space-x-4">
                                        <span className={`w-8 h-8 ${colorSet.accent} text-white text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5`}>
                                            {index + 1}
                                        </span>
                                        <p className="text-gray-800 leading-relaxed font-medium">
                                            {announcement}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Call to Action coloré */}
            <section className="py-16 bg-gradient-to-r from-primary-blue to-primary-green text-white">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h2 className="text-3xl font-light mb-4">
                        Notre Engagement
                    </h2>
                    <div className="w-16 h-1 bg-primary-yellow mx-auto mb-8"></div>
                    <p className="text-xl mb-8 max-w-2xl mx-auto opacity-95">
                        Transformer cette vision en réalité à travers des actions concrètes
                        et une gouvernance exemplaire pour tous les Gabonais.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => window.location.href = '/adhesion'}
                            className="bg-primary-yellow text-gray-900 px-8 py-4 font-semibold hover:bg-white transition-colors inline-flex items-center justify-center space-x-2 group"
                        >
                            <span>REJOINDRE LE MOUVEMENT</span>
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>

                    </div>
                </div>
            </section>
        </div>
    );
};

export default VisionPage;
